import {
  StatsigProvider as LibStatsigProvider,
  StatsigClient,
} from "@statsig/react-bindings";
import { StatsigSessionReplayPlugin } from "@statsig/session-replay";
import React, { useContext, useEffect, useMemo, useRef } from "react";
import type { ClientInitializeResponse } from "statsig-node-lite/dist/Evaluator";

import { ABTestingId } from "@every.org/common/src/codecs/entities";
import { assertEnvPresent } from "@every.org/common/src/helpers/getEnv";

import { AuthContext } from "src/context/AuthContext";
import { getStableId, getTestingId } from "src/utility/abtesting";
import { getClientBotType } from "src/utility/helpers";
import { isDevOrTest } from "src/utility/runtimeEnvironment";
import { setStatsigClient } from "src/utility/statsigClient";

// This points to a cloudflare worker that proxies requests to statsig
// TODO - probably simpler and maybe more performant to use vercel / nextjs to
// proxy these rather than a cloudflare worker
// https://docs.statsig.com/client/javascript-sdk/next-js#advanced---network-proxy
const STATSIG_PROXY_URL = "https://p.every.org/v1";

type StatsigProviderProps = {
  children: React.ReactNode;
  statsigInitProps?: Record<string, unknown>;
  abTestingId?: string;
};

export const StatsigProvider = ({
  children,
  statsigInitProps,
  abTestingId = undefined,
}: StatsigProviderProps) => {
  const authState = useContext(AuthContext);
  const testingId = getTestingId({
    authState,
    ssrTestingId: abTestingId,
  }) as ABTestingId;
  const stableId =
    (statsigInitProps as ClientInitializeResponse)?.user?.customIDs?.stableID ||
    getStableId();
  const nonprofitId = (statsigInitProps as ClientInitializeResponse)?.user
    ?.customIDs?.nonprofitId;

  // Note, in dev we see a warning about Statsig being initialized twice This
  // shouldn't happen in prod, as React renders everything twice in dev due to
  // Strict mode
  // https://legacy.reactjs.org/docs/strict-mode.html#detecting-unexpected-side-effects
  const clientRef = useRef<StatsigClient | null>(null);

  const client = useMemo(() => {
    if (clientRef.current) {
      return clientRef.current;
    }

    const inst = new StatsigClient(
      assertEnvPresent(
        process.env.NEXT_PUBLIC_STATSIG_PUBLIC_API_KEY,
        "STATSIG_API_KEY"
      ),
      {
        userID: testingId,
        customIDs: {
          ...(statsigInitProps as ClientInitializeResponse)?.user?.customIDs,
          ...(stableId ? { stableID: stableId } : {}),
        },
      },
      {
        environment: {
          tier: process.env.NEXT_PUBLIC_ENVIRONMENT_NAME,
        },
        networkConfig: {
          // Allows us to proxy statsig requests through our own domain
          api: STATSIG_PROXY_URL,
        },
        // Skip replay for CI, local dev, and bots such as Ghost Inspector
        plugins:
          isDevOrTest || getClientBotType()
            ? []
            : [new StatsigSessionReplayPlugin()],
      }
    );
    clientRef.current = inst;
    setStatsigClient(inst);
    if (statsigInitProps) {
      inst.dataAdapter.setData(JSON.stringify(statsigInitProps));
      inst.initializeSync();
    } else {
      inst.initializeAsync();
    }
    return inst;
  }, [statsigInitProps, testingId, stableId]);

  useEffect(() => {
    const currentUserId = client?.getContext().user.userID;
    const currentCustomIds = client?.getContext().user.customIDs || {};
    const currentNonprofitId = currentCustomIds.nonprofitId;
    if (
      (currentUserId && testingId !== currentUserId) ||
      (nonprofitId && nonprofitId !== currentNonprofitId)
    ) {
      client.updateUserAsync({
        userID: testingId,
        customIDs: {
          ...currentCustomIds,
          ...(nonprofitId ? { nonprofitId } : {}),
        },
      });
      client.initializeAsync();
    }
  }, [testingId, client, nonprofitId]);

  return <LibStatsigProvider client={client}>{children}</LibStatsigProvider>;
};
